import React, { useMemo } from 'react';
import styled from 'styled-components';
import Colors from '@components/shared/Colors';
import { BodyLG, BodySM } from '@components/shared/TextTypes';
import TwoColumn from '@components/shared/TwoColumn';
import { PageSlice, SliceVmsAtHomeDetails, SliceVmsAtHomeItem } from '@contracts';
import useNavigate from '@utils/useNavigate';
import useReplacedInnerHtml from '@utils/useReplacedInnerHtml';

interface Content extends PageSlice {
  at_home: PageSlice[];
}

interface Props {
  slice?: PageSlice;
  content?: Content;
}

const VmsAtHome: React.FC<Props> = ({ content, slice }) => {
  const { navigateWrapper } = useNavigate();
  const primary = useMemo(() => (content || slice?.primary || {}) as SliceVmsAtHomeDetails, []);
  const textHtml = useReplacedInnerHtml(primary.at_home_text?.text);
  const items = useMemo(() => {
    if (content?.at_home) {
      return content.at_home.map((slice) => ({ id: slice.id, ...slice.primary })) as SliceVmsAtHomeItem[];
    }

    return slice?.items as SliceVmsAtHomeItem[];
  }, []);

  const renderIconBox = (item: SliceVmsAtHomeItem) => (
    <IconBox key={item.id || item.item?.text}>
      <ImageBox>
        <img src={item.icon?.url} />
      </ImageBox>
      <IconTextWrap>
        <IconText $longText={item.item?.text} $shortText={item.short?.text} />
      </IconTextWrap>
    </IconBox>
  );

  return (
    <Container
      $padding={primary.at_home_padding?.text}
      $mobilePadding={primary.at_home_mobile_padding?.text}
      $background={primary.at_home_background?.text}
    >
      <Inner>
        <TwoColumn gap="70px" mobileReverse={false} breakpoint="1024px" alignment="center">
          <StyledImg src={primary.at_home_image?.url} />
          <TextWrap>
            <Title>{primary.at_home_subtitle?.text}</Title>
            <StyledBodyLG style={{ marginBottom: 0 }} dangerouslySetInnerHTML={{ __html: textHtml }} />
            <IconBoxWrap>{items.map((item) => renderIconBox(item))}</IconBoxWrap>

            <Button onClick={() => navigateWrapper(primary.at_home_button_link?.url)}>{primary.at_home_button?.text}</Button>
            <StyledBodySM style={{ marginTop: '20px' }}>{primary.at_home_disclaimer?.text}</StyledBodySM>
          </TextWrap>
        </TwoColumn>
      </Inner>
    </Container>
  );
};

export default VmsAtHome;

const Container = styled.div<{ $padding: string; $mobilePadding: string; $background: string }>`
  background: ${(props) => props.$background || 'none'};
  padding: ${(props) => props.$padding || '43px 0 0 0'};

  @media (max-width: 1024px) {
    padding: ${(props) => props.$mobilePadding || '8px 30px 0'};
  }
`;

const Inner = styled.div`
  margin: 0 auto;
`;

const TextWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0;
  gap: 20px;
  max-width: 372px;
  color: ${Colors.navyBlue};

  @media (max-width: 1024px) {
    align-items: center;
    max-width: none;
  }
`;

const Title = styled.h3`
  font-family: 'Domaine';
  font-style: normal;
  font-weight: normal;
  font-size: 24px;
  line-height: 140%;
  color: inherit;

  @media (max-width: 1024px) {
    text-align: center;
    font-size: 24px;
  }
`;

const IconBoxWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0;
  gap: 20px;
  @media (max-width: 1024px) {
    flex-direction: row;
    gap: 20px;
    max-width: 400px;
  }
`;

const IconBox = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 20px;
  color: ${Colors.navyBlue};
  @media (max-width: 1024px) {
    flex-direction: column;
  }

  @media (max-width: 1024px) {
    align-items: center;
    padding: 0 0 0 0;
  }
`;

const ImageBox = styled.div`
  display: flex;
  width: 25px;
  height: 25px;
  align-items: center;
  flex-direction: column;
`;

const IconTextWrap = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 5px;

  & > span.fade {
    opacity: 0.5;
  }
`;

const StyledBodyLG = styled(BodyLG)`
  font-family: 'BrownStd';
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 150%;

  text-align: left;
  margin-bottom: 20px;
  color: inherit;

  @media (max-width: 1024px) {
    text-align: center;
    margin-bottom: 0;
  }
`;

const StyledBodySM = styled(BodySM)`
  opacity: 0.5;
  @media (max-width: 1024px) {
    text-align: center;
  }
`;

const StyledImg = styled.img`
  width: 100%;
  max-width: 594px;

  @media (max-width: 1024px) {
    margin-bottom: 40px;
  }
`;

const Button = styled.div`
  margin-top: 10px;
  padding: 17px 25px;
  gap: 10px;
  background: ${Colors.lilac};
  border-radius: 2px;
  flex: none;
  flex-grow: 0;
  font-family: 'BrownStd';
  font-style: normal;
  font-weight: 700;
  font-size: 14px;
  line-height: 16px;
  align-items: center;
  text-align: center;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: ${Colors.cream};
  width: fit-content;
  cursor: pointer;

  &:hover {
    background: ${Colors.darkGreen};
  }
`;

export const IconText = styled.span<{ $longText: string; $shortText: string }>`
  font-family: BrownStd;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0;
  text-align: left;
  width: 260px;

  &:after {
    content: '${(p) => p.$longText}';
  }

  @media (max-width: 1024px) {
    font-weight: 700;
    font-size: 14px;
    line-height: 140%;
    width: unset;
    max-width: 200px;
    text-align: center;
    &:after {
      content: '${(p) => p.$shortText}';
    }
  }
`;
